'use strict';

import Vue from 'vue'
import inventoryService from './services/inventory-service';
import image from './services/image-manipulator';
import calus from 'calus';
import {find, values} from 'lodash';
import * as IntersectionObserver from 'intersection-observer';  // eslint-disable-line no-unused-vars

const SELECTOR = '.js-calendar-widget';

const init = () => {
    if (document.querySelector(SELECTOR) === null) {
        return false;
    }
    const calendarConfig = {
        el: document.querySelector('.calendar-dates__body'),
        displayInColumn: true,
        timezone: 'America/New_York',
        onSelect: date => {
            var element = document.querySelector('#date-' + date.time.toFormat('y-LL-dd'));
            if (element) {
                element.classList.add('calendar-item--highlight');
                if (window.IntersectionObserver) {
                    const observer = new window.IntersectionObserver(entries => {
                        let [entry] = entries;
                        if (entry.isIntersecting) {
                            element.classList.remove('calendar-item--highlight');
                            observer.unobserve(element);
                        }
                    });
                    observer.observe(element);
                } else {
                    setTimeout(() => {
                        element.classList.remove('calendar-item--highlight');
                    }, 100);
                }
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
            document.querySelector('.calendar-dates__close').click();
        },
    };

    const calendar = new calus(calendarConfig);

    const vm = new Vue({
        el: SELECTOR,
        data: {
            grouped_results: [],
            all_results: [],
            loading: true,
            sections: [],
            total_results: 0,
        },
        watch: {
            grouped_results: newVal => {
                calendar.setAvailable(values(newVal).map(group => {
                    return group.date_obj.toISO();
                }));
            },
        },
        computed: {
            hiddenKeywordCategories: function () {
                return [
                    // 'Calendar Category'
                ]
            },
            hiddenKeywordIDs: function () {
                return (this.$el.dataset.hiddenKeywordIds ?
                    this.$el.dataset.hiddenKeywordIds.split(',') :
                    [])
                        .map(id => parseInt(id))
                        .filter(id => id);
            }
        },
        methods: {
            optionChange: () => {
                vm.filterResults();
            },

            filterResults: () => {
                vm.total_results = 0;

                vm.grouped_results = vm.all_results.reduce((groupedResults, perf) => {
                    if (perf.isVisible()) {
                        const perfDate = perf.perf_date_luxon.toFormat('yyyyLLdd');
                        if (!groupedResults[perfDate]) {
                            groupedResults[perfDate] = {
                                id: `date-${perf.perf_date_luxon.toFormat('yyyy-MM-dd')}`,    // date-2019-{{month}}-{{date}}
                                aria_control: `calendar-popup-${perf.perf_date_luxon.toFormat('yyyy-MM-dd')}`,   // calendar-popup
                                day: perf.perf_date_luxon.toFormat('cccc'),
                                month: perf.perf_date_luxon.toFormat('LLL'),
                                date: perf.perf_date_luxon.toFormat('d'),
                                date_obj: perf.perf_date_luxon,
                                events: [],
                            };
                        }
                        groupedResults[perfDate].events.push(perf);
                        vm.total_results++;
                    }
                    return groupedResults;
                }, {});

                vm.updateFilterCounts();
            },

            updateFilterCounts: () => {
                if (vm.sections.length) {
                    vm.sections.forEach(section => {
                        section.options.forEach(opt => opt.count = vm.all_results.filter(event => {
                            return event.isVisible(opt);
                        }).length);
                    });
                }
            }
        },
    });

    inventoryService.getInventory().then(data => {
        const relatedPerformances = data.reduce((carry, perf) => {
            if (perf.web_contents && perf.web_contents.RelatedPerformance && parseInt(perf.web_contents.RelatedPerformance, 10) > 0) {
                carry.push(perf.perf_no);
            }
            return carry;
        }, []);
        vm.all_results = data.map(perf => {
            perf.event_id = `calendar-popup-container-${perf.perf_no || btoa(perf.booking_link).replace(/=/g, '')}`;
            perf.toggle_selector = `#${perf.event_id}`;  // #calendar-popup-container

            if (relatedPerformances.indexOf(perf.perf_no) !== -1) {
                perf.listing_image = undefined;
            } else {
                if ('listing_image' in perf) {
                    perf.display_image = function(config) {
                        return this.listing_image ? image.process(this.listing_image, config) : '';
                    };
                }
            }

            if ('performed_with' in perf) {
                perf.performed_with = perf.performed_with.map(ballet => {
                    ballet.display_image = config => {
                        return ballet.listing_image ? image.process(ballet.listing_image, config) : '';
                    };
                    return ballet;
                });
            }

            /**
             * @param optionToTest If passed then will test this option
             * @retrun Boolean
             */
            perf.isVisible = function(optionToTest) {
                if (vm.sections.length === 0) {
                    return true;
                }
                const visibleSections = vm.sections.filter(section => {
                    let checkedOptions = section.options.filter(opt => (opt.checked || (optionToTest && opt.name === optionToTest.name)));
                    if (checkedOptions.length === 0) {
                        return true;
                    }

                    return checkedOptions.filter(opt => opt.perf_nos.indexOf(this.perf_no) > -1).length > 0;
                });

                return visibleSections.length === vm.sections.length;
            };
            return perf;
        });

        vm.filterResults();

        vm.sections = vm.all_results.reduce((keywords, event) => {
            event.keywords.forEach(keyword => {
                if (vm.hiddenKeywordCategories.indexOf(keyword.category) > -1) {
                    return;
                }
                if (vm.hiddenKeywordIDs.indexOf(keyword.keyword_no) > -1) {
                    return;
                }

                let kwcategory = find(keywords, kwc => kwc.title === keyword.category);

                if (!kwcategory) {
                    kwcategory = {
                        title: keyword.category,
                        options: [],
                    };
                    keywords.push(kwcategory);
                }

                let kwOption = find(kwcategory.options, opt => opt.name === keyword.keyword);

                if (!kwOption) {
                    kwOption = {
                        category: kwcategory,
                        name: keyword.keyword,
                        count: 0,
                        checked: false,
                        perf_nos: [],
                        // Sort will use the `sort` field or fall back to alphabetical
                        sort: keyword.sort ? keyword.sort : keyword.keyword.charCodeAt(0),
                    };
                    kwcategory.options.push(kwOption);
                }

                // List of performances
                kwOption.perf_nos.push(event.perf_no);
            });
            return keywords;
        }, []);
        vm.sections.forEach(section => {
            section.options.sort((a, b) => a.sort - b.sort);
        });

        vm.updateFilterCounts();
    }).finally(() => {
        vm.loading = false;
    });
}

init();
